import { z } from 'zod';

/**
 * Persistent search object (RD-105/RD-106, entities D1/D2) — one search state
 * carried as URL params + session across home → fleet → detail → booking.
 *
 * URL params: `from`, `to`, `pickup`, `return`, `age`, `addons`. Times are 30-minute
 * granularity (D1); `from < to`; `age` is the driver age feeding the
 * young-driver surcharge (D2, RD-304). Validation lives here so the widget,
 * the SSR readers, and the booking domain share one contract.
 */

/** Driver-age bounds — generous; the young-driver *surcharge* threshold is a
 * separate admin-tunable rule (Open Q1), not a hard validation bound here. */
export const DRIVER_AGE_MIN = 18;
export const DRIVER_AGE_MAX = 99;

/** A datetime-local string at 30-minute granularity, e.g. `2026-06-12T10:00`. */
export const thirtyMinDateTime = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:(00|30)$/, 'time must be on a 30-minute slot');

export const searchObjectSchema = z
  .object({
    pickupLocationId: z.string().min(1),
    returnLocationId: z.string().min(1),
    fromAt: thirtyMinDateTime,
    toAt: thirtyMinDateTime,
    driverAge: z.coerce.number().int().min(DRIVER_AGE_MIN).max(DRIVER_AGE_MAX),
    /** Add-ons picked in the search, keyed by the catalogue `key` (not the uuid,
     * so a shared link stays readable and survives a re-seed). Optional: a search
     * is complete without extras. */
    addons: z.record(z.string(), z.number().int().positive()).optional(),
  })
  .refine((s) => s.fromAt < s.toAt, {
    message: 'return must be after pickup',
    path: ['toAt'],
  });

export type SearchObject = z.infer<typeof searchObjectSchema>;

/** Partial/in-progress search (widget state before submit). */
export const partialSearchSchema = searchObjectSchema.innerType().partial();
export type PartialSearch = z.infer<typeof partialSearchSchema>;

/** Serialize a (partial) search object to the canonical `/frota?...` params. */
export function searchToParams(s: PartialSearch): URLSearchParams {
  const qs = new URLSearchParams();
  if (s.fromAt) qs.set('from', s.fromAt);
  if (s.toAt) qs.set('to', s.toAt);
  if (s.pickupLocationId) qs.set('pickup', s.pickupLocationId);
  if (s.returnLocationId) qs.set('return', s.returnLocationId);
  if (s.driverAge != null) qs.set('age', String(s.driverAge));
  const addons = Object.entries(s.addons ?? {}).filter(([, n]) => n > 0);
  if (addons.length > 0) qs.set('addons', addons.map(([k, n]) => `${k}:${n}`).join(','));
  return qs;
}

/** Parse `/frota?...` params (or a plain record) back into a partial search. */
export function paramsToSearch(params: URLSearchParams | Record<string, string | undefined>): PartialSearch {
  const get = (k: string) => (params instanceof URLSearchParams ? (params.get(k) ?? undefined) : params[k]);
  const out: PartialSearch = {};
  const from = get('from');
  const to = get('to');
  const pickup = get('pickup');
  const ret = get('return');
  const age = get('age');
  const addons = get('addons');
  if (from) out.fromAt = from;
  if (to) out.toAt = to;
  if (pickup) out.pickupLocationId = pickup;
  if (ret) out.returnLocationId = ret;
  if (age != null && age !== '') {
    const n = Number(age);
    if (Number.isFinite(n)) out.driverAge = n;
  }
  if (addons) {
    const picked: Record<string, number> = {};
    for (const pair of addons.split(',')) {
      const [key, raw] = pair.split(':');
      const n = Number(raw);
      if (key && Number.isInteger(n) && n > 0) picked[key] = n;
    }
    if (Object.keys(picked).length > 0) out.addons = picked;
  }
  return out;
}

/** True when a partial search has every field needed to run availability. */
export function isCompleteSearch(s: PartialSearch): s is SearchObject {
  return searchObjectSchema.safeParse(s).success;
}
